import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { 
  FaChevronLeft, 
  FaShieldAlt, 
  FaTruck, 
  FaUndo, 
  FaCheck, 
  FaMicrochip, 
  FaCamera, 
  FaBatteryFull,
  FaMemory,
  FaWifi
} from 'react-icons/fa';
import { productsData } from '../../data/productsData';
import { API_CONFIG } from '../../config/api';
import './Products.css';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedStorage, setSelectedStorage] = useState('256GB');

  useEffect(() => {
    // Look up in productsData first
    const matchedProduct = productsData.find(
      (p) => String(p.id) === String(id) || p.slug === String(id)
    );

    if (matchedProduct) {
      setProduct(matchedProduct);
      if (matchedProduct.colors && matchedProduct.colors.length > 0) {
        setSelectedColor(matchedProduct.colors[0]);
      }
    }

    // Try fetching from API as well
    fetch(`${API_CONFIG.BASE_URL}/products.php?id=${id}`)
      .then(res => res.json())
      .then(apiData => {
        if (apiData && apiData.name) {
          const merged = {
            ...(matchedProduct || productsData[0]),
            ...apiData,
            images: apiData.images && apiData.images.length > 0
              ? apiData.images.map(img => ({
                  ...img,
                  image_path: API_CONFIG.getImageUrl(img.image_path)
                }))
              : (matchedProduct ? matchedProduct.images : productsData[0].images)
          };
          setProduct(merged);
        }
      })
      .catch(() => {
        // Use matched local data
        if (!matchedProduct) {
          setProduct(productsData[0]);
        }
      });
  }, [id]);

  if (!product) {
    return (
      <div className="product-detail-page">
        <Container className="py-5 text-center">
          <div className="catalog-spinner" />
          <p className="mt-3 text-secondary">Loading device specifications...</p>
        </Container>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 
    ? product.images 
    : [{ id: 0, image_path: '/Photo/i7 air 2.jpg', label: 'Main' }];

  const storageOptions = ['128GB', '256GB', '512GB', '1TB'];

  return (
    <div className="product-detail-page">
      {/* Sub-navigation breadcrumb */}
      <div className="detail-breadcrumb-bar">
        <Container>
          <div className="breadcrumb-content">
            <Link to="/products" className="back-link">
              <FaChevronLeft className="back-arrow" /> Back to All Products
            </Link>
            <span className="detail-series-tag">{product.series}</span>
          </div>
        </Container>
      </div>

      <Container className="py-5">
        <Row className="gy-5 align-items-start">
          {/* Left Column: Interactive Image Gallery */}
          <Col lg={7} md={12}>
            <div className="detail-gallery-wrapper">
              {/* Main Featured Image Stage */}
              <div className="detail-main-stage">
                <img 
                  src={images[currentImageIndex].image_path} 
                  alt={product.name}
                  className="detail-featured-image"
                />
                <div className="gallery-badge-row">
                  <span className="gallery-badge">{product.badge || 'Peace Original'}</span>
                  {product.isNew && <span className="gallery-new-tag">New Release</span>}
                </div>
              </div>

              {/* Thumbnails Navigation Row */}
              {images.length > 1 && (
                <div className="detail-thumbnails-row">
                  {images.map((img, idx) => (
                    <button
                      key={img.id || idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`detail-thumb-btn ${idx === currentImageIndex ? 'active' : ''}`}
                      aria-label={`Switch to angle ${idx + 1}`}
                    >
                      <img 
                        src={img.image_path} 
                        alt={`${product.name} angle ${idx + 1}`}
                        className="thumb-img"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Col>

          {/* Right Column: Title, Copywriting, Specs, & Purchasing */}
          <Col lg={5} md={12}>
            <div className="detail-info-card">
              <div className="detail-header-block">
                <span className="detail-eyebrow">{product.series}</span>
                <h1 className="detail-title">{product.name}</h1>
                {product.tagline && (
                  <p className="detail-tagline">{product.tagline}</p>
                )}
                
                <div className="detail-pricing-box">
                  <div className="detail-price-main">{product.price}</div>
                  {product.monthlyPrice && (
                    <div className="detail-price-monthly">{product.monthlyPrice}</div>
                  )}
                </div>
              </div>

              <hr className="detail-divider" />

              {/* Benefit-Driven Description */}
              <div className="detail-section-block">
                <h4 className="detail-block-title">Overview</h4>
                <p className="detail-description-text">
                  {product.description}
                </p>
              </div>

              {/* Color Finish Selector */}
              {product.colors && product.colors.length > 0 && (
                <div className="detail-section-block">
                  <div className="finish-title-row">
                    <span className="detail-block-title">Finish: </span>
                    <strong className="active-finish-name">
                      {selectedColor ? selectedColor.name : product.colors[0].name}
                    </strong>
                  </div>
                  <div className="detail-color-swatches">
                    {product.colors.map((color, idx) => {
                      const isSelected = selectedColor?.name === color.name;
                      return (
                        <button
                          key={idx}
                          onClick={() => setSelectedColor(color)}
                          className={`detail-swatch-btn ${isSelected ? 'selected' : ''}`}
                          style={{ backgroundColor: color.hex }}
                          title={color.name}
                          aria-label={color.name}
                        >
                          {isSelected && <FaCheck className="swatch-check-icon" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Storage Capacity Selector */}
              <div className="detail-section-block">
                <span className="detail-block-title">Capacity</span>
                <div className="storage-options-grid">
                  {storageOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setSelectedStorage(opt)}
                      className={`storage-btn ${selectedStorage === opt ? 'active' : ''}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Key Features List */}
              {product.keyFeatures && (
                <div className="detail-section-block">
                  <h4 className="detail-block-title">Highlights</h4>
                  <ul className="detail-highlights-list">
                    {product.keyFeatures.map((feat, idx) => (
                      <li key={idx}>
                        <span className="feat-bullet" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Buy Actions */}
              <div className="detail-cta-group">
                <button className="apple-btn apple-btn-primary detail-buy-btn">
                  Add to Cart • {product.price}
                </button>
                <Link to="/products" className="apple-btn apple-btn-secondary detail-secondary-btn">
                  Continue Shopping
                </Link>
              </div>

              {/* Guarantee Perks */}
              <div className="detail-perks-box">
                <div className="detail-perk">
                  <FaTruck className="perk-icon" />
                  <span>Free Express Delivery</span>
                </div>
                <div className="detail-perk">
                  <FaUndo className="perk-icon" />
                  <span>14-Day Free Returns</span>
                </div>
                <div className="detail-perk">
                  <FaShieldAlt className="perk-icon" />
                  <span>2-Year PeaceCare Included</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Bottom Section: Comprehensive Technical Specifications Table */}
        {product.specs && (
          <Row className="mt-5">
            <Col xs={12}>
              <div className="detail-specs-table-card">
                <div className="specs-table-header">
                  <h3>Technical Specifications</h3>
                  <span className="specs-table-model">{product.name}</span>
                </div>

                <div className="specs-detail-grid">
                  {product.specs.display && (
                    <div className="specs-detail-row">
                      <div className="specs-row-label">
                        <FaMicrochip className="spec-row-icon" /> Display & Resolution
                      </div>
                      <div className="specs-row-val">{product.specs.display}</div>
                    </div>
                  )}

                  {product.specs.processor && (
                    <div className="specs-detail-row">
                      <div className="specs-row-label">
                        <FaMicrochip className="spec-row-icon" /> Processor & Silicon
                      </div>
                      <div className="specs-row-val">{product.specs.processor}</div>
                    </div>
                  )}

                  {product.specs.camera && (
                    <div className="specs-detail-row">
                      <div className="specs-row-label">
                        <FaCamera className="spec-row-icon" /> Camera System
                      </div>
                      <div className="specs-row-val">{product.specs.camera}</div>
                    </div>
                  )}

                  {product.specs.battery && (
                    <div className="specs-detail-row">
                      <div className="specs-row-label">
                        <FaBatteryFull className="spec-row-icon" /> Battery & Charging
                      </div>
                      <div className="specs-row-val">{product.specs.battery}</div>
                    </div>
                  )}

                  {product.specs.storage && (
                    <div className="specs-detail-row">
                      <div className="specs-row-label">
                        <FaMemory className="spec-row-icon" /> Storage Configurations
                      </div>
                      <div className="specs-row-val">{product.specs.storage}</div>
                    </div>
                  )}

                  {product.specs.material && (
                    <div className="specs-detail-row">
                      <div className="specs-row-label">
                        <FaShieldAlt className="spec-row-icon" /> Craftsmanship & Materials
                      </div>
                      <div className="specs-row-val">{product.specs.material}</div>
                    </div>
                  )}

                  {product.specs.connectivity && (
                    <div className="specs-detail-row">
                      <div className="specs-row-label">
                        <FaWifi className="spec-row-icon" /> Cellular & Wireless
                      </div>
                      <div className="specs-row-val">{product.specs.connectivity}</div>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default ProductDetail;
